import { Head, router } from "@inertiajs/react";
import { Activity, Download, Share2 } from "lucide-react";
import { useMemo } from "react";

import { DateRangeSelector } from "@/components/analytics/date-range-selector";
import { PLATFORM_COLORS, PlatformBreakdownChart } from "@/components/analytics/platform-breakdown-chart";
import { PlatformIcon } from "@/components/analytics/platform-icon";
import { SharesOverTimeChart } from "@/components/analytics/shares-over-time-chart";
import { StatsCards } from "@/components/analytics/stats-cards";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AppLayout from "@/layouts/app-layout";
import { analyticsExportPath, analyticsPath } from "@/routes";
import type {
  AnalyticsDomainDatum,
  AnalyticsPlatformDatum,
  AnalyticsRangeOption,
  AnalyticsSharesOverTime,
  AnalyticsTopPage,
  BreadcrumbItem,
} from "@/types";

const numberFormatter = new Intl.NumberFormat();
const averageFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

interface AnalyticsPageProps {
  selected_range: string;
  available_ranges: AnalyticsRangeOption[];
  total_shares: number;
  average_shares_per_day: number;
  shares_over_time: AnalyticsSharesOverTime;
  shares_by_platform: AnalyticsPlatformDatum[];
  shares_by_domain: AnalyticsDomainDatum[];
  top_pages: AnalyticsTopPage[];
  metadata: {
    range: {
      start: string;
      end: string;
      days: number;
    };
  };
  selected_page?: string;
}

export default function Analytics(props: AnalyticsPageProps) {
  const {
    selected_range,
    available_ranges,
    total_shares,
    average_shares_per_day,
    shares_over_time,
    shares_by_platform,
    shares_by_domain,
    top_pages,
    metadata,
    selected_page,
  } = props;

  const breadcrumbs: BreadcrumbItem[] = [{ title: "Analytics", href: analyticsPath() }];

  const hasData = total_shares > 0;

  const activeRangeLabel = available_ranges.find((option) => option.key === selected_range)?.label;

  const rangeSummary = `${dateFormatter.format(new Date(metadata.range.start))} – ${dateFormatter.format(new Date(metadata.range.end))}`;

  const stats = useMemo(() => {
    const days = metadata.range.days;
    return [
      {
        key: "total_shares",
        title: "Total shares",
        value: numberFormatter.format(total_shares),
        icon: Share2,
      },
      {
        key: "avg_per_day",
        title: "Shares per day",
        value: averageFormatter.format(average_shares_per_day),
        description: days > 1 ? `Average across ${days} days` : undefined,
        icon: Activity,
      },
    ];
  }, [average_shares_per_day, metadata.range.days, total_shares]);

  const handleRangeChange = (value: string) => {
    if (value === selected_range) return;

    router.get(
      analyticsPath(),
      { range: value, page: selected_page || undefined },
      { preserveState: true, preserveScroll: true, replace: true },
    );
  };

  const handlePageFilterSelect = (url: string) => {
    router.get(
      analyticsPath(),
      { range: selected_range, page: url },
      { preserveState: true, preserveScroll: true, replace: true },
    );
  };

  const handleClearPageFilter = () => {
    if (!selected_page) return;

    router.get(
      analyticsPath(),
      { range: selected_range },
      { preserveState: true, preserveScroll: true, replace: true },
    );
  };

  const extractHostname = (value: string) => {
    try {
      return new URL(value).hostname;
    } catch {
      return value;
    }
  };

  const renderPlatformPill = (platform: string, label: string) => {
    const color = PLATFORM_COLORS[platform] ?? PLATFORM_COLORS.other;
    return (
      <span
        key={platform + label}
        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium"
        style={{
          backgroundColor: `${color}1a`,
          color,
          border: `1px solid ${color}33`,
        }}
      >
        <PlatformIcon platform={platform} className="size-3.5" />
        {label}
      </span>
    );
  };

  const exportUrl = useMemo(() => {
    const searchParams = new URLSearchParams({ range: selected_range });
    if (selected_page) {
      searchParams.set("page", selected_page);
    }
    return `${analyticsExportPath()}?${searchParams.toString()}`;
  }, [selected_page, selected_range]);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Analytics" />
      <div className="space-y-8 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Analytics</h1>
            <p className="text-muted-foreground mt-1 max-w-xl text-sm">
              Track where your OG images are being shared and which platforms drive the most attention.
            </p>
            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {activeRangeLabel ?? "Selected range"} • {rangeSummary}
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:items-end lg:justify-end">
            <div className="flex items-center gap-3">
              {selected_page ? (
                <div className="flex items-center gap-2 rounded-md border border-dashed border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-100">
                  <span className="font-medium">Filtered by page:</span>
                  <span className="truncate text-[11px] font-semibold md:text-xs">{selected_page}</span>
                  <Button variant="ghost" size="sm" onClick={handleClearPageFilter}>
                    Clear
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">Click a page below to filter analytics by URL.</p>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <DateRangeSelector value={selected_range} options={available_ranges} onChange={handleRangeChange} />
              <Button variant="outline" asChild>
                <a href={exportUrl} className="inline-flex items-center gap-2">
                  <Download className="size-4" />
                  Export data
                </a>
              </Button>
            </div>
          </div>
        </div>

        {hasData ? (
          <div className="space-y-6">
            <StatsCards items={stats} />

            <div className="grid gap-6 lg:grid-cols-2 2xl:grid-cols-3">
              <Card className="lg:col-span-2 2xl:col-span-2">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg font-semibold">Shares over time</CardTitle>
                  <CardDescription>Daily share activity for the selected time range</CardDescription>
                </CardHeader>
                <CardContent>
                  <SharesOverTimeChart data={shares_over_time.data} granularity={shares_over_time.granularity} />
                </CardContent>
              </Card>

              <Card className="lg:col-span-2 2xl:col-span-1">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg font-semibold">Platform breakdown</CardTitle>
                  <CardDescription>Where your previews are being shared</CardDescription>
                </CardHeader>
                <CardContent>
                  <PlatformBreakdownChart data={shares_by_platform} />
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg font-semibold">Top domains</CardTitle>
                  <CardDescription>Share of total shares by domain</CardDescription>
                </CardHeader>
                <CardContent>
                  {shares_by_domain.length ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Domain</TableHead>
                          <TableHead className="text-right">Shares</TableHead>
                          <TableHead className="text-right">Share</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shares_by_domain.map((item) => {
                          const share = total_shares ? Math.round((item.count / total_shares) * 100) : 0;
                          return (
                            <TableRow key={item.domain}>
                              <TableCell className="font-medium">{item.display_name}</TableCell>
                              <TableCell className="text-right">{numberFormatter.format(item.count)}</TableCell>
                              <TableCell className="text-muted-foreground text-right">{share}%</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">No domain data recorded yet</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg font-semibold">Top pages</CardTitle>
                  <CardDescription>
                    URLs that received the most social shares. Click a page to filter the analytics.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {top_pages.length ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Page</TableHead>
                          <TableHead className="text-right">Shares</TableHead>
                          <TableHead className="text-right">Platforms</TableHead>
                          <TableHead className="text-right">Filter</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {top_pages.map((page) => (
                          <TableRow key={page.url}>
                            <TableCell className="max-w-[240px] truncate align-top">
                              <div className="flex flex-col gap-1">
                                <a
                                  href={page.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-orange-600 hover:underline"
                                >
                                  {page.url}
                                </a>
                                <span className="text-muted-foreground text-xs">
                                  {page.domain ?? extractHostname(page.url)}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right align-top">
                              {numberFormatter.format(page.shares)}
                            </TableCell>
                            <TableCell className="text-right align-top">
                              <div className="flex flex-wrap justify-end gap-1">
                                {page.platforms
                                  .slice(0, 4)
                                  .map((platform) => renderPlatformPill(platform.platform, platform.label))}
                                {page.platforms.length > 4 ? (
                                  <span className="text-muted-foreground text-xs">
                                    +{page.platforms.length - 4} more
                                  </span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-right align-top">
                              <Button
                                variant={page.url === selected_page ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => handlePageFilterSelect(page.url)}
                                className="min-w-[88px]"
                              >
                                {page.url === selected_page ? "Selected" : "Filter"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-sm">No page level data for this range</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="border-dashed">
            <CardHeader className="space-y-2 text-center">
              <CardTitle className="text-2xl font-semibold">No analytics yet</CardTitle>
              <CardDescription>
                Once social platforms crawl your OG images you will see detailed engagement analytics right here.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-muted-foreground flex flex-col items-center gap-3 text-sm">
              <p>Share your content on social platforms or use our integration guide to start collecting data.</p>
              <ul className="list-disc space-y-1 text-left">
                <li>Verify that your domains are connected</li>
                <li>Embed the provided meta tags on your site</li>
                <li>Share a few URLs to kick off tracking</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
